import {alpha2ToAlpha3, getName} from 'i18n-iso-countries';

import {useMemo} from 'react';

import {isNotNil, sort} from 'ramda';

import {getSafely} from 'shared/web';

import {FALLBACK_LANGUAGE} from '../../../i18n/fallbackLanguage';
import i18n from '../../../i18n/i18n';
import {useGetApiCountriesQuery} from '../../../store/catalogueApi';

export type MarketOption = {value: string; label: string};

const LANGUAGE_TO_MARKET: Record<string, string> = {
  cs: 'CZE',
  de: 'DEU',
  en: 'GBR',
  es: 'ESP',
  fr: 'FRA',
  it: 'ITA',
  nl: 'NLD',
  pl: 'POL',
  sk: 'SVK',
};

const DEFAULT_MARKET_FALLBACK = getSafely(LANGUAGE_TO_MARKET, FALLBACK_LANGUAGE) ?? 'CZE';

function getLocaleForCountryNames(resolvedLanguage: string): string {
  return resolvedLanguage.includes('-') ? resolvedLanguage.split('-')[0] : resolvedLanguage;
}

function getDefaultMarket(
  availableCodes: string[],
  locale: string,
  navigatorLanguage: string
): string {
  const regionAlpha2 = navigatorLanguage.includes('-') ? navigatorLanguage.split('-')[1] : null;
  const fromRegion = isNotNil(regionAlpha2)
    ? alpha2ToAlpha3(regionAlpha2.toUpperCase())
    : undefined;
  if (isNotNil(fromRegion) && availableCodes.includes(fromRegion)) {
    return fromRegion;
  }
  const fromLanguage = getSafely(LANGUAGE_TO_MARKET, locale);
  if (isNotNil(fromLanguage) && availableCodes.includes(fromLanguage)) {
    return fromLanguage;
  }
  return availableCodes[0] ?? DEFAULT_MARKET_FALLBACK;
}

function buildMarketOptions(availableCodes: string[], locale: string): MarketOption[] {
  if (availableCodes.length === 0) {
    return [];
  }
  const withLabels = availableCodes.map((value) => ({
    value,
    label: getName(value, locale) ?? value,
  }));
  return sort((a, b) => a.label.localeCompare(b.label), withLabels);
}

export function useImportMarket(): {
  marketOptions: MarketOption[];
  defaultMarket: string;
  isLoading: boolean;
} {
  const {data: apiCountries, isLoading} = useGetApiCountriesQuery({
    format: 'iso3',
  });
  const availableCodes = useMemo(
    () =>
      isNotNil(apiCountries) && typeof apiCountries === 'object' ? Object.keys(apiCountries) : [],
    [apiCountries]
  );
  const localeForNames = getLocaleForCountryNames(i18n?.resolvedLanguage ?? FALLBACK_LANGUAGE);
  const defaultMarket = useMemo(
    () =>
      getDefaultMarket(
        availableCodes,
        localeForNames,
        typeof navigator !== 'undefined' ? navigator.language : ''
      ),
    [availableCodes, localeForNames]
  );
  const marketOptions = useMemo(
    () => buildMarketOptions(availableCodes, localeForNames),
    [availableCodes, localeForNames]
  );
  return {marketOptions, defaultMarket, isLoading};
}
